import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SiteHeader from '../components/layout/SiteHeader';
import PageBreadcrumb from '../components/layout/PageBreadcrumb';
import SiteLayout, { SiteMain } from '../components/layout/SiteLayout';
import SupportSubNav from '../components/layout/SupportSubNav';
import PageHero from '../components/ui/PageHero';
import {
  SUPPORT_EMAIL,
  SUPPORT_PHONE,
  HELP_CENTER_PATH,
  getWhatsAppUrl
} from '../constants/siteNav';

const HELP_SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting started',
    icon: 'fa-rocket',
    summary: 'New to Niko Vibe? Start here.',
    articles: [
      {
        title: 'What is Niko Vibe?',
        body: `Niko Vibe is Kenya's premier event ticketing platform. We help you discover concerts, conferences, festivals, sports, and cultural experiences across Africa - and buy tickets securely with instant delivery.

Whether you're an attendee looking for your next experience or an organizer ready to sell tickets, everything starts on our homepage at nikovibe.com.`,
      },
      {
        title: 'How to create an account (Sign up)',
        body: `Creating an account takes less than two minutes and gives you access to your tickets, payment history, and account settings.

**Step by step:**
1. Click **Sign up** in the top navigation (or visit /register).
2. Enter your full name, email address, and a secure password.
3. Submit the form - you'll be signed in automatically.
4. Visit **My tickets** from your account menu to see purchases anytime.

**Tip:** Use the same email at checkout even if you buy as a guest - we'll link tickets to your account when you sign up later.`,
        links: [{ to: '/register', label: 'Create your account' }],
      },
      {
        title: 'How to sign in',
        body: `Already have an account? Signing in lets you view tickets, download PDFs, and manage your profile.

**Step by step:**
1. Click **Sign in** in the top navigation (or visit /login).
2. Enter the email and password you used when registering.
3. After signing in, open **My tickets** from the account menu in the header.

**Forgot your password?** Contact us at ${SUPPORT_EMAIL} or WhatsApp and we'll help you regain access.`,
        links: [{ to: '/login', label: 'Sign in to your account' }],
      },
    ],
  },
  {
    id: 'buying-tickets',
    title: 'Buying tickets',
    icon: 'fa-ticket-alt',
    summary: 'Find events and complete your purchase.',
    articles: [
      {
        title: 'How to find and browse events',
        body: `Discover events in several ways:

• **Homepage** - scroll to "Upcoming events" or use the featured carousel.
• **Browse events** - see the full catalogue with filters and search.
• **Categories** - explore by type: music, sports, tech, food & drink, and more.
• **Search** - use the search bar in the header to find events by name or location.

Click any event card to open its detail page with date, venue, pricing, and ticket packages.`,
        links: [{ to: '/events', label: 'Browse all events' }],
      },
      {
        title: 'How to buy a ticket',
        body: `Once you've found an event you love, purchasing is straightforward:

**Step by step:**
1. Open the event page and review date, time, venue, and ticket tiers.
2. Select your ticket package(s) and quantity.
3. Click **Get Tickets** to proceed to checkout.
4. Enter your name, email, and phone number (required for M-Pesa and ticket delivery).
5. Choose **M-Pesa** or your preferred payment method and complete payment.
6. On success, your ticket is delivered instantly - check your email and **My tickets** dashboard.

**Guest checkout:** You don't need an account to buy, but we recommend signing up so tickets are saved to your profile.`,
        links: [{ to: '/events', label: 'Find an event' }],
      },
      {
        title: 'Payment methods & M-Pesa',
        body: `Niko Vibe supports secure mobile payments tailored for Kenya:

• **M-Pesa** - enter your number, approve the prompt on your phone, and you're in.
• Payments are processed in Kenyan Shillings (KES).
• You'll receive a confirmation once payment is verified.

**If M-Pesa fails:** Wait a moment and try again, or ensure your line has sufficient funds. Still stuck? WhatsApp us with your event name and phone number - we'll assist within business hours.`,
      },
      {
        title: 'Ticket confirmation & email delivery',
        body: `After a successful payment:

• You'll see a confirmation screen with your order summary.
• A confirmation email is sent to the address you provided at checkout.
• Each ticket includes a unique QR code for entry validation at the venue.
• Signed-in users can also access tickets from **Dashboard → My tickets**.

**Didn't receive email?** Check spam/junk folders, then contact us with your payment phone number and event name.`,
      },
    ],
  },
  {
    id: 'tracking-tickets',
    title: 'Tracking & managing tickets',
    icon: 'fa-qrcode',
    summary: 'Access, download, and present your tickets.',
    articles: [
      {
        title: 'How to track your tickets',
        body: `Your tickets live in one place once you're signed in:

**Step by step:**
1. Sign in to your account.
2. Click your profile name → **My tickets** (or visit /dashboard).
3. You'll see all active tickets with event name, date, venue, and status.
4. Click any ticket to view details, QR code, and download options.

**Bought as a guest?** Sign up or sign in with the same email used at checkout - your tickets will appear in your dashboard.`,
        links: [{ to: '/dashboard', label: 'Go to My tickets' }],
      },
      {
        title: 'Downloading & presenting tickets',
        body: `At the venue, you'll need your ticket QR code:

• Open the ticket from your dashboard or confirmation email.
• Show the QR code on your phone screen - brightness up works best.
• You can download a PDF copy for offline access where available.
• Each ticket is valid for one entry unless stated otherwise on the event page.

**Lost your ticket?** Sign in to My tickets or contact us with your purchase email and phone number.`,
      },
      {
        title: 'Payment history',
        body: `View past transactions from your account dashboard:

1. Sign in and open **Dashboard**.
2. Switch to the **Payments** tab to see order history, amounts, and statuses.
3. Use this for expense records or if you need help locating a specific purchase.

For receipts or invoice requests, email ${SUPPORT_EMAIL} with your order details.`,
        links: [{ to: '/dashboard', label: 'View payment history' }],
      },
    ],
  },
  {
    id: 'organizers',
    title: 'Listing & managing events',
    icon: 'fa-calendar-plus',
    summary: 'For organizers selling tickets on Niko Vibe.',
    articles: [
      {
        title: 'How to list your event',
        body: `Organizers with admin or organizer access can create events directly on the platform:

**Step by step:**
1. Sign in with an organizer or admin account.
2. Click **List your event** in the header (or visit /admin/events/create).
3. Fill in event title, description, category, date/time, and venue.
4. Upload a high-quality poster image (recommended 540×675 px).
5. Set ticket tiers, prices, and capacity.
6. Publish - your event goes live on nikovibe.com once approved.

**New organizer?** Contact us to get your account upgraded. We onboard venues, promoters, and brands across Kenya.`,
        links: [{ to: '/admin/events/create', label: 'Create an event' }],
      },
      {
        title: 'Managing ticket sales & attendees',
        body: `Once your event is live, use the admin panel to:

• Monitor ticket sales and revenue in real time.
• View attendee lists and export data.
• Update event details, pricing tiers, or capacity.
• Mark events as sold out or handle cancellations.

Access everything from **Admin panel** after signing in with an organizer account.`,
        links: [{ to: '/admin/dashboard', label: 'Open admin panel' }],
      },
      {
        title: 'Organizer payouts & support',
        body: `We work closely with event organizers on payouts, pricing strategy, and on-site check-in.

• Payout schedules and terms are agreed during onboarding.
• For payout questions, email ${SUPPORT_EMAIL} or WhatsApp our team.
• Need help with QR scanning at the door? We provide guidance and tools for smooth entry.

**Ready to partner?** Reach out - we'd love to power your next event.`,
      },
    ],
  },
  {
    id: 'account',
    title: 'Account & security',
    icon: 'fa-user-shield',
    summary: 'Profile, passwords, and privacy.',
    articles: [
      {
        title: 'Updating your profile',
        body: `Keep your contact details current so tickets and updates reach you:

1. Sign in and go to **Dashboard**.
2. Update your name, email, or phone as needed.
3. Save changes - future tickets will use the updated information.

Your data is handled per our Privacy Policy. We never sell personal information to third parties.`,
        links: [
          { to: '/dashboard', label: 'Account settings' },
          { to: '/privacy-policy', label: 'Privacy Policy' },
        ],
      },
      {
        title: 'Signing out & shared devices',
        body: `If you used a public or shared computer, always sign out after viewing tickets:

• Click your profile name → **Sign out**.
• On mobile, clear the browser session if needed.

Your tickets remain accessible when you sign back in with the same credentials.`,
      },
    ],
  },
  {
    id: 'refunds',
    title: 'Refunds & cancellations',
    icon: 'fa-undo-alt',
    summary: 'When events change or you need a refund.',
    articles: [
      {
        title: 'Event cancelled by the organizer',
        body: `If an event is cancelled, we'll notify ticket holders by email. Refund policies vary by event:

• Refunds are typically processed to the original payment method.
• Processing may take 3–7 business days depending on M-Pesa and bank timelines.
• Contact the organizer or our support team if you haven't heard within a week of cancellation.

Cancelled events show a clear notice on nikovibe.com - tickets cannot be purchased for cancelled listings.`,
      },
      {
        title: 'Requesting a refund',
        body: `Refund eligibility depends on the event organizer's policy:

1. Check the event page or your confirmation email for refund terms.
2. Contact the organizer first for event-specific requests.
3. If unresolved, email ${SUPPORT_EMAIL} or WhatsApp us with:
   • Event name and date
   • Your purchase email and phone number
   • Reason for the refund request

We review each case and respond within 1–2 business days.`,
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: 'fa-wrench',
    summary: 'Common issues and quick fixes.',
    articles: [
      {
        title: 'Payment didn\'t go through',
        body: `Try these steps before contacting support:

• Confirm you approved the M-Pesa prompt on your phone.
• Check that your M-Pesa balance covers the ticket total plus fees.
• Ensure you entered the correct M-Pesa number.
• Wait 2–3 minutes - sometimes confirmation is delayed.
• Don't pay twice; contact us if you're unsure whether payment succeeded.

Include your phone number and event name when reaching out.`,
      },
      {
        title: 'Can\'t sign in or reset password',
        body: `• Double-check you're using the email from registration (not a typo).
• Passwords are case-sensitive.
• Try signing in on a different browser or clear cache.
• For password reset, email ${SUPPORT_EMAIL} with your registered email - we'll verify and assist.`,
      },
      {
        title: 'Event page or ticket not loading',
        body: `• Refresh the page or try a different browser.
• Check your internet connection.
• For sold-out or cancelled events, the page may show limited information by design.
• If an image or page fails repeatedly, WhatsApp us with the event link and a screenshot.`,
      },
    ],
  },
];

const QUICK_TOPICS = HELP_SECTIONS.map(({ id, title, icon }) => ({ id, title, icon }));

function renderBody(text) {
  return text.split('\n\n').map((paragraph, i) => {
    const lines = paragraph.split('\n');
    const isList = lines.every((l) => l.startsWith('• ') || l.startsWith('- '));
    if (isList) {
      return (
        <ul key={i} className="list-disc list-outside ml-5 space-y-1.5 text-gray-600 text-sm leading-relaxed">
          {lines.map((line, j) => (
            <li key={j}>{line.replace(/^[•-]\s*/, '')}</li>
          ))}
        </ul>
      );
    }
    const parts = paragraph.split(/(\*\*[^*]+\*\*)/g);
    return (
      <p key={i} className="text-gray-600 text-sm leading-relaxed">
        {parts.map((part, j) =>
          part.startsWith('**') && part.endsWith('**') ? (
            <strong key={j} className="font-semibold text-gray-800">
              {part.slice(2, -2)}
            </strong>
          ) : (
            part
          )
        )}
      </p>
    );
  });
}

const HelpCenterPage = () => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('getting-started');
  const [expandedArticles, setExpandedArticles] = useState({});

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && HELP_SECTIONS.some((s) => s.id === hash)) {
      setActiveSection(hash);
    }
  }, [location.hash]);

  const toggleArticle = (sectionId, index) => {
    const key = `${sectionId}-${index}`;
    setExpandedArticles((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const scrollToSection = (id) => {
    setActiveSection(id);
    window.history.replaceState(null, '', `${HELP_CENTER_PATH}#${id}`);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <SiteLayout className="bg-gray-50">
      <SiteHeader />
      <PageBreadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Help' }]} />
      <SupportSubNav />

      <SiteMain>
        <PageHero
          eyebrow="Help Center"
          title="How can we help you?"
          subtitle="Guides for buying tickets, managing your account, listing events, and more."
        />

      {/* Contact strip */}
      <section className="relative z-10 -mt-6 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid sm:grid-cols-3 gap-4">
            <a
              href={getWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-4 bg-white rounded-xl p-5 shadow-lg border border-gray-100 hover:border-emerald-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center shrink-0 shadow-md group-hover:scale-105 transition-transform">
                <i className="fab fa-whatsapp text-white text-xl" />
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600 mb-1">Fastest response</p>
                <p className="font-semibold text-gray-900 text-sm">WhatsApp us</p>
                <p className="text-gray-500 text-xs mt-0.5">{SUPPORT_PHONE}</p>
              </div>
            </a>

            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="group flex items-start gap-4 bg-white rounded-xl p-5 shadow-lg border border-gray-100 hover:border-orange-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center shrink-0 group-hover:bg-orange-200 transition-colors">
                <i className="fas fa-envelope text-orange-600 text-lg" />
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-orange-600 mb-1">Email support</p>
                <p className="font-semibold text-gray-900 text-sm">Write to us</p>
                <p className="text-gray-500 text-xs mt-0.5">{SUPPORT_EMAIL}</p>
              </div>
            </a>

            <a
              href={`tel:${SUPPORT_PHONE.replace(/\s/g, '')}`}
              className="group flex items-start gap-4 bg-white rounded-xl p-5 shadow-lg border border-gray-100 hover:border-orange-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-gray-900 flex items-center justify-center shrink-0 group-hover:bg-gray-800 transition-colors">
                <i className="fas fa-phone-alt text-orange-400 text-lg" />
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Phone support</p>
                <p className="font-semibold text-gray-900 text-sm">Call our team</p>
                <p className="text-gray-500 text-xs mt-0.5">{SUPPORT_PHONE}</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      <div className="flex-1 container mx-auto max-w-6xl px-4 py-12 w-full">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Sidebar */}
          <aside className="lg:w-64 shrink-0">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-3 px-1">
                  Browse topics
                </p>
                <nav className="space-y-1" aria-label="Help topics">
                  {QUICK_TOPICS.map(({ id, title, icon }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => scrollToSection(id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium rounded-lg text-left transition-colors ${
                        activeSection === id
                          ? 'bg-orange-50 text-orange-700 border border-orange-100'
                          : 'text-gray-600 hover:bg-white hover:text-gray-900 border border-transparent'
                      }`}
                    >
                      <i className={`fas ${icon} w-4 text-xs ${activeSection === id ? 'text-orange-500' : 'text-gray-400'}`} />
                      {title}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="hidden lg:block bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-5 text-white">
                <p className="text-xs font-semibold uppercase tracking-wider text-orange-400 mb-2">Still need help?</p>
                <p className="text-sm text-gray-300 leading-relaxed mb-4">
                  Our team typically responds on WhatsApp within a few hours during business days.
                </p>
                <a
                  href={getWhatsAppUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold bg-[#25D366] hover:bg-[#20BA5A] px-4 py-2.5 rounded-lg transition-colors w-full justify-center"
                >
                  <i className="fab fa-whatsapp" />
                  Start a chat
                </a>
              </div>
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0 space-y-12">
            {HELP_SECTIONS.map((section) => (
              <section key={section.id} id={section.id} className="scroll-mt-28">
                <div className="flex items-start gap-4 mb-6 pb-4 border-b border-gray-200">
                  <div className="w-11 h-11 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                    <i className={`fas ${section.icon} text-orange-600`} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{section.title}</h2>
                    <p className="text-sm text-gray-500 mt-0.5">{section.summary}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {section.articles.map((article, index) => {
                    const key = `${section.id}-${index}`;
                    const isOpen = expandedArticles[key] ?? index === 0;

                    return (
                      <article
                        key={key}
                        className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden hover:border-orange-100 transition-colors"
                      >
                        <button
                          type="button"
                          onClick={() => toggleArticle(section.id, index)}
                          className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50/80 transition-colors"
                          aria-expanded={isOpen}
                        >
                          <h3 className="font-semibold text-gray-900 text-sm md:text-base pr-2">
                            {article.title}
                          </h3>
                          <i
                            className={`fas fa-chevron-down text-gray-400 text-xs shrink-0 transition-transform duration-200 ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </button>

                        {isOpen && (
                          <div className="px-5 pb-5 pt-0 space-y-3 border-t border-gray-50">
                            <div className="pt-4 space-y-3">{renderBody(article.body)}</div>
                            {article.links?.length > 0 && (
                              <div className="flex flex-wrap gap-2 pt-2">
                                {article.links.map((link) => (
                                  <Link
                                    key={link.to}
                                    to={link.to}
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-100 px-3 py-1.5 rounded-lg transition-colors"
                                  >
                                    {link.label}
                                    <i className="fas fa-arrow-right text-[9px]" />
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}

            {/* Bottom CTA */}
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 md:p-10 text-center">
              <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-headset text-orange-600 text-xl" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Didn&apos;t find what you need?</h2>
              <p className="text-gray-600 text-sm max-w-lg mx-auto mb-6 leading-relaxed">
                Our support team is ready to help with tickets, payments, account access, and organizer enquiries.
                Reach us on WhatsApp for the quickest reply, or email us anytime.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={getWhatsAppUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white px-6 py-3 rounded-lg font-semibold text-sm transition-colors shadow-md"
                >
                  <i className="fab fa-whatsapp text-lg" />
                  Chat on WhatsApp
                </a>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-lg font-semibold text-sm transition-colors"
                >
                  <i className="fas fa-envelope text-sm" />
                  {SUPPORT_EMAIL}
                </a>
              </div>
              <p className="text-xs text-gray-400 mt-5">
                Phone: {SUPPORT_PHONE} · Available 24/7
              </p>
            </section>
          </main>
        </div>
      </div>
      </SiteMain>
    </SiteLayout>
  );
};

export default HelpCenterPage;
